
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Megaphone, Trash2, Clock, Link as LinkIcon, Smile, Image, Plus, History } from "lucide-react";
import { format, formatDistanceToNow, isPast } from "date-fns";
import { ptBR } from "date-fns/locale";

const ICON_OPTIONS = [
  { value: "megaphone", label: "📢 Megafone" },
  { value: "warning", label: "⚠️ Atenção" },
  { value: "info", label: "ℹ️ Info" },
  { value: "star", label: "⭐ Destaque" },
  { value: "rocket", label: "🚀 Novidade" },
  { value: "gift", label: "🎁 Promoção" },
  { value: "bell", label: "🔔 Notificação" },
  { value: "fire", label: "🔥 Urgente" },
  { value: "check", label: "✅ Concluído" },
  { value: "calendar", label: "📅 Evento" },
];

const EMOJI_OPTIONS = ["📢", "⚠️", "ℹ️", "⭐", "🚀", "🎁", "🔔", "🔥", "✅", "📅", "💡", "🎉", "📌", "🛑", "💰", "📣", "🏆", "❤️", "👋", "🆕"];

interface Announcement {
  id: string;
  icon: string | null;
  emoji: string | null;
  message: string;
  link_url: string | null;
  link_text: string | null;
  expires_at: string;
  is_active: boolean;
  created_at: string;
  created_by: string | null;
}

const AnnouncementsTab = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Form state
  const [emoji, setEmoji] = useState("📢");
  const [message, setMessage] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");
  const [expiresIn, setExpiresIn] = useState("24"); // hours
  const [noExpiry, setNoExpiry] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from("announcements")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      toast.error("Erro ao carregar avisos");
      console.error(error);
    } else {
      setAnnouncements(data || []);
    }
    setIsLoading(false);
  };

  const handleCreate = async () => {
    if (!message.trim()) {
      toast.error("Digite uma mensagem para o aviso");
      return;
    }

    setIsSaving(true);
    let expiresAt: string | null = null;
    if (!noExpiry) {
      const d = new Date();
      d.setHours(d.getHours() + parseInt(expiresIn));
      expiresAt = d.toISOString();
    }

    const { error } = await supabase.from("announcements").insert({
      emoji,
      message: message.trim(),
      link_url: linkUrl.trim() || null,
      link_text: linkText.trim() || null,
      expires_at: expiresAt,
      is_active: true,
    });

    if (error) {
      toast.error("Erro ao criar aviso");
      console.error(error);
    } else {
      toast.success("Aviso criado com sucesso!");
      setMessage("");
      setLinkUrl("");
      setLinkText("");
      setExpiresIn("24");
      setNoExpiry(false);
      setEmoji("📢");
      fetchAnnouncements();
    }
    setIsSaving(false);
  };

  const handleToggle = async (id: string, isActive: boolean) => {
    const { error } = await supabase
      .from("announcements")
      .update({ is_active: !isActive })
      .eq("id", id);

    if (error) {
      toast.error("Erro ao atualizar aviso");
    } else {
      toast.success(isActive ? "Aviso desativado" : "Aviso ativado");
      fetchAnnouncements();
    }
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from("announcements").delete().eq("id", id);
    if (error) {
      toast.error("Erro ao excluir aviso");
    } else {
      toast.success("Aviso excluído");
      fetchAnnouncements();
    }
  };

  const activeAnnouncements = announcements.filter(a => a.is_active && (!a.expires_at || !isPast(new Date(a.expires_at))));
  const historyAnnouncements = announcements.filter(a => !a.is_active || (a.expires_at && isPast(new Date(a.expires_at))));

  return (
    <div className="space-y-6">
      {/* Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-blue-600" />
            Criar Novo Aviso
          </CardTitle>
          <CardDescription>
            Crie um aviso global que aparecerá no topo de todas as páginas para todos os usuários
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Emoji picker */}
          <div className="space-y-2">
            <Label>Emoji / Ícone</Label>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                className="text-2xl h-12 w-12"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              >
                {emoji}
              </Button>
              <span className="text-sm text-muted-foreground">Clique para trocar o emoji</span>
            </div>
            {showEmojiPicker && (
              <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-muted/30 max-w-md">
                {EMOJI_OPTIONS.map((e) => (
                  <button
                    key={e}
                    onClick={() => { setEmoji(e); setShowEmojiPicker(false); }}
                    className="text-2xl hover:scale-125 transition-transform cursor-pointer p-1 rounded hover:bg-muted"
                  >
                    {e}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Label>Mensagem do Aviso *</Label>
            <Textarea
              placeholder="Ex: Manutenção programada para hoje às 22h..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
            />
          </div>

          {/* Link */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <LinkIcon className="h-3.5 w-3.5" />
                URL do Link (opcional)
              </Label>
              <Input
                placeholder="https://exemplo.com"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Texto do Link (opcional)</Label>
              <Input
                placeholder="Saiba mais"
                value={linkText}
                onChange={(e) => setLinkText(e.target.value)}
              />
            </div>
          </div>

          {/* Timer */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Switch checked={noExpiry} onCheckedChange={setNoExpiry} />
              <Label>Sem limite de tempo (aviso permanente)</Label>
            </div>
            {!noExpiry && (
              <>
                <Label className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />
                  Duração do Aviso (horas)
                </Label>
                <div className="flex items-center gap-3">
                  <Input
                    type="number"
                    min="1"
                    max="720"
                    value={expiresIn}
                    onChange={(e) => setExpiresIn(e.target.value)}
                    className="w-32"
                  />
                  <span className="text-sm text-muted-foreground">
                    {parseInt(expiresIn) >= 24
                      ? `≈ ${Math.round(parseInt(expiresIn) / 24)} dia(s)`
                      : `${expiresIn} hora(s)`}
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Preview */}
          {message && (
            <div className="space-y-2">
              <Label>Pré-visualização</Label>
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-3 rounded-lg flex items-center gap-3 text-sm">
                <span className="text-lg">{emoji}</span>
                <span className="flex-1">{message}</span>
                {linkUrl && (
                  <span className="underline font-medium whitespace-nowrap">
                    {linkText || "Saiba mais"}
                  </span>
                )}
                {!noExpiry && (
                  <span className="text-xs opacity-75 whitespace-nowrap flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {expiresIn}h
                  </span>
                )}
                {noExpiry && (
                  <span className="text-xs opacity-75 whitespace-nowrap">∞ Permanente</span>
                )}
              </div>
            </div>
          )}

          <Button onClick={handleCreate} disabled={isSaving || !message.trim()} className="w-full md:w-auto">
            <Plus className="h-4 w-4 mr-2" />
            {isSaving ? "Criando..." : "Publicar Aviso"}
          </Button>
        </CardContent>
      </Card>

      {/* Active */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-green-600" />
            Avisos Ativos ({activeAnnouncements.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activeAnnouncements.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-4">Nenhum aviso ativo no momento</p>
          ) : (
            <div className="space-y-3">
              {activeAnnouncements.map((a) => (
                <div key={a.id} className="border rounded-lg p-4 flex items-center gap-4">
                  <span className="text-2xl">{a.emoji || "📢"}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{a.message}</p>
                    {a.link_url && (
                      <a href={a.link_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                        {a.link_text || a.link_url}
                      </a>
                    )}
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-xs">
                        {a.expires_at ? (
                          <>
                            <Clock className="h-3 w-3 mr-1" />
                            Expira {formatDistanceToNow(new Date(a.expires_at), { addSuffix: true, locale: ptBR })}
                          </>
                        ) : (
                          "∞ Permanente"
                        )}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={a.is_active} onCheckedChange={() => handleToggle(a.id, a.is_active)} />
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(a.id)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-gray-500" />
            Histórico de Avisos ({historyAnnouncements.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {historyAnnouncements.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-4">Nenhum aviso no histórico</p>
          ) : (
            <div className="space-y-2">
              {historyAnnouncements.map((a) => (
                <div key={a.id} className="border rounded-lg p-3 flex items-center gap-3 opacity-60">
                  <span className="text-lg">{a.emoji || "📢"}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{a.message}</p>
                    <p className="text-xs text-muted-foreground">
                      Criado em {format(new Date(a.created_at), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                      {" · "}
                      {a.expires_at && isPast(new Date(a.expires_at)) ? "Expirado" : "Desativado"}
                    </p>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(a.id)}>
                    <Trash2 className="h-4 w-4 text-red-400" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AnnouncementsTab;
